import {
	TreeView,
	TreeViewItem
} from "client/Components";
import { AppStoreDialog } from "client/Features/Apps/Store/AppStoreDialog";
import {
	EMPTY_ARRAY,
	isNil
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import {
	SettingsLayoutModel,
	SettingsPageKind
} from "client/Api/SettingsLayout/settingsLayout";
import {
	ReactElement,
	ReadonlyRefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import { userSettings } from "./userSettings";
import {
	GroupItem,
	ItemModel,
	filterSettingsStructure,
	findPathToRoot
} from "./structure";
import { environmentSettings } from "./environmentSettings";
import { never } from "client_legacy/ndaw/errors";

export type SidebarMenuProps = {
	selectedItemId: SettingsPageKind | undefined;
	model: SettingsLayoutModel | null;
	onSidebarNavigation?(): void;
};

export const SidebarMenu = (props: SidebarMenuProps): ReactElement => {
	const {
		selectedItemId,
		model,
		onSidebarNavigation
	} = props;

	const [isAppStoreVisible, setIsAppStoreVisible] = useState<boolean>(false);
	const initialSelectedItemIdRef: ReadonlyRefObject<SettingsPageKind | undefined> = useRef(selectedItemId);

	const availableIdSet = useMemo(
		(): ReadonlySet<SettingsPageKind> => {
			return new Set(model?.availablePages ?? EMPTY_ARRAY);
		},
		[model]
	);

	const isAvailableToUser = useCallback(
		(item: ItemModel): boolean => {
			if (item.kind === "group") {
				return true;
			}

			return availableIdSet.has(item.id);
		},
		[availableIdSet]
	);

	const items = useMemo(
		(): readonly ItemModel[] => filterSettingsStructure(
			[
				userSettingsItem,
				environmentSettingsItem
			],
			isAvailableToUser
		),
		[isAvailableToUser]
	);

	const treeItems = useMemo(
		(): readonly TreeViewItem<ItemModel>[] => items.map(convertToTreeViewItem),
		[items]
	);

	const selectedItem = useMemo(
		(): ItemModel | undefined => {
			if (isNil(selectedItemId)) {
				return;
			}

			const item: ItemModel | undefined = findItem(items, selectedItemId);

			return item;
		},
		[items, selectedItemId]
	);

	const initiallyExpandedNodes = useMemo(
		(): readonly ItemModel[] => {
			const id: SettingsPageKind | nil = initialSelectedItemIdRef.current;
			const rootNodes: readonly ItemModel[] = [
				userSettingsItem,
				environmentSettingsItem
			];

			if (isNil(id)) {
				return rootNodes;
			}

			const path: readonly ItemModel[] | undefined = findPathToRoot(
				items,
				id
			);

			if (!path) {
				console.warn(`There's no path to the selected item with id ${id}`);
			}

			return path
				? [
					...rootNodes,
					...path
				]
				: rootNodes;
		},
		[items]
	);

	const clickItemHandler = useCallback(
		(item: ItemModel): void => {
			switch (item.kind) {
				case "group":
					break;
				case "app-store":
					setIsAppStoreVisible(true);
					onSidebarNavigation?.();
					break;
				case "link":
					onSidebarNavigation?.();
					break;
				default:
					never(item);
			}
		},
		[onSidebarNavigation]
	);

	return (
		<>
			<TreeView<ItemModel>
				value={selectedItem}
				items={treeItems}
				compareBy="id"
				onItemClick={clickItemHandler}
				trackingId="sidebar-user-settings"
				initiallyExpandedNodes={initiallyExpandedNodes}
			/>
			{isAppStoreVisible && (
				<AppStoreDialog
					onClose={() => setIsAppStoreVisible(false)}
				/>
			)}
		</>
	);
};
SidebarMenu.displayName = "SidebarMenu";

function convertToTreeViewItem(
	item: ItemModel
): TreeViewItem<ItemModel> {
	return {
		title: item.name,
		value: item,
		content: item.name,
		icon: item.kind === "group"
			? item.icon
			: undefined,
		children: item.kind === "group"
			? item.children.map(convertToTreeViewItem)
			: undefined,
		navigation: item.kind === "link"
			? item.navigation
			: undefined
	};
}

function findItem(
	items: readonly ItemModel[],
	itemId: SettingsPageKind
): ItemModel | undefined {
	for (const item of items) {
		if (typeof item.id !== "string" && item.id === itemId) {
			return item;
		}

		if (item.kind === "group") {
			const childrenResult: ItemModel | undefined = findItem(item.children, itemId);

			if (childrenResult) {
				return childrenResult;
			}
		}
	}

	return undefined;
}

const USER_SETTINGS_ITEM_ID: Guid = newGuid();
const ENVIRONMENT_SETTING_ITEM_ID: Guid = newGuid();

const userSettingsItem: GroupItem = {
	id: USER_SETTINGS_ITEM_ID,
	kind: "group",
	name: localize("_UserSettings"),
	icon: "user",
	children: userSettings
};

const environmentSettingsItem: GroupItem = {
	id: ENVIRONMENT_SETTING_ITEM_ID,
	kind: "group",
	// NOTE: This was intentionally hardcoded, because design does not want to neither localize nor translate this value
	name: "Workspace",
	icon: "workspace",
	children: environmentSettings
};